import { Directive, HostBinding, HostListener, inject, Input } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { TurnoverService } from '@app/+authenticated/+account/settings/insights/turnover/services/turnover.service';
import { PermissionCheckPipe } from '@app/pipes/permission-flag.pipe';
import { TeamService } from '@app/reducers/orm/team/team.service';
import { TranslateService } from '@ngx-translate/core';
import { SbDialogService } from '@sb/ui/dialog';
import { catchError, EMPTY, of, switchMap, take } from 'rxjs';

@Directive({
  selector: '[appDeleteTeam]',
  providers: [PermissionCheckPipe],
})
export class DeleteTeamDirective {
  private dialog = inject(SbDialogService);
  private router = inject(Router);
  private route = inject(ActivatedRoute);
  private permissionCheck = inject(PermissionCheckPipe);
  private teamService = inject(TeamService);
  private translate = inject(TranslateService);
  private turnoverService = inject(TurnoverService);

  @Input()
  set appDeleteTeam(context: { teamId: string; departmentId: string }) {
    this.teamId = context.teamId;
    this.departmentId = context.departmentId;

    if (!this.teamId || !this.departmentId) {
      throw new Error('Team ID and Department ID are required in appDeleteTeam directive');
    }
  }

  @HostBinding('class')
  class = 'cursor-pointer';

  @HostListener('click', ['$event'])
  onClick(event: MouseEvent) {
    event.stopPropagation();
    this.removeTeam();
  }

  private teamId: string;
  private departmentId: string;

  removeTeam() {
    const hasMoveEmployeesPermission = this.permissionCheck.transform(['Edit users', 'Manage account']);

    this.turnoverService
      .showDeactivatedAllocationReviewDialogIfNeeded({ entityType: 'team', entityId: this.teamId })
      .pipe(
        switchMap(() => this.teamService.validateRemove(this.teamId)),
        catchError((error) => {
          if (error.status === 422 && hasMoveEmployeesPermission) {
            this.triggerTeamDeletionFlow(this.teamId);
          }
          return EMPTY;
        }),
        switchMap(() => this.confirmRemoveTeamDialog()),
        switchMap((result) => {
          if (result?.confirmed) {
            return this.teamService.remove(this.teamId, this.departmentId);
          }

          return of(null);
        }),
        take(1),
      )
      .subscribe(() => {
        if (this.route.outlet === 'modal') {
          this.dialog.closeAll();
        }
      });
  }

  private confirmRemoveTeamDialog() {
    return this.dialog.openConfirm({
      type: 'danger',
      title: this.translate.instant('Delete This Team?'),
      description: this.translate.instant(
        `Deleting this team will remove all associated schedules. The team's timesheet data will be preserved. This action cannot be undone. Are you sure you want to continue?`,
      ),
      primary: {
        text: this.translate.instant('Delete team'),
      },
    }).closed;
  }

  private triggerTeamDeletionFlow(teamId: string) {
    if (this.route.outlet === 'modal') {
      this.dialog.closeAll();
    }
    this.router.navigate(['', { outlets: { modal: ['delete-team', teamId] } }]);
  }
}
